import { Injectable } from "@angular/core";
import { StateService } from "@core-services/state.service";
import { Observable } from "rxjs";
import {
	PriceVerificationModel,
	SplitWarningModel,
	TaxesUndeclaredSingleModel,
	TaxesUndeclaredTrxModel
} from "@modules/taxes/domain/entities/taxes.model";

export interface TaxesUndeclaredTrxState {
	trxMulti: TaxesUndeclaredTrxModel[];
	trxSingle: TaxesUndeclaredTrxModel[];
	trxSingleData: TaxesUndeclaredSingleModel;
	isLoading: boolean;
	isValidationLoading: boolean;
	higherThanFirstBoundary: boolean;
	higherThanSecondBoundary: boolean;
	isCoinSwapped: boolean;
	isSplitFlow: boolean;
	sliceNumber: number;
	amountLeft: number;
	splitWarning: SplitWarningModel[];
}

const initialState: TaxesUndeclaredTrxState = {
	trxMulti: [],
	trxSingle: [],
	trxSingleData: null,
	isLoading: false,
	isValidationLoading: false,
	higherThanFirstBoundary: false,
	higherThanSecondBoundary: false,
	isCoinSwapped: false,
	isSplitFlow: false,
	sliceNumber: 0,
	amountLeft: null,
	splitWarning: []
};

@Injectable({
	providedIn: "root"
})
export class TaxesUndeclaredTrxStateService extends StateService<TaxesUndeclaredTrxState> {
	public constructor() {
		super(initialState);
	}

	trxMulti$: Observable<TaxesUndeclaredTrxModel[]> = this.select((state) => state.trxMulti);
	trxSingle$: Observable<TaxesUndeclaredTrxModel[]> = this.select((state) => state.trxSingle);
	trxSingleData$: Observable<TaxesUndeclaredSingleModel> = this.select((state) => state.trxSingleData);
	isLoading$: Observable<boolean> = this.select((state) => state.isLoading);
	isValidationLoading$: Observable<boolean> = this.select((state) => state.isValidationLoading);
	isCoinSwapped$: Observable<boolean> = this.select((state) => state.isCoinSwapped);
	higherThanFirstBoundary$: Observable<boolean> = this.select((state) => state.higherThanFirstBoundary);
	higherThanSecondBoundary$: Observable<boolean> = this.select((state) => state.higherThanSecondBoundary);
	sliceNumber$: Observable<number> = this.select((state) => state.sliceNumber);
	amountLeft$: Observable<number> = this.select((state) => state.amountLeft);
	isSplitDeposit$: Observable<boolean> = this.select((state) => state.isSplitFlow);
	splitWarning$: Observable<SplitWarningModel[]> = this.select((state) => state.splitWarning);

	public updateTrxMulti(trxMulti: TaxesUndeclaredTrxModel[]): void {
		this.setState({
			trxMulti
		});
	}

	public updateTrxSingle(trxSingle: TaxesUndeclaredTrxModel): void {
		this.setState({
			trxSingle: [trxSingle]
		});
	}

	public updateTrxSingleData(trxSingle: TaxesUndeclaredTrxModel, trxSingleData: TaxesUndeclaredSingleModel): void {
		this.setState({
			trxSingleData,
			trxSingle: [trxSingle],
			amountLeft: +trxSingleData.assetAmount
		});
	}

	public setIsLoading(isLoading: boolean): void {
		this.setState({
			isLoading
		});
	}

	public updateStateNonSplit(response: PriceVerificationModel, trxSingle: TaxesUndeclaredTrxModel): void {
		this.setState({
			isValidationLoading: false,
			higherThanFirstBoundary: response.higherThanFirstBoundary,
			higherThanSecondBoundary: response.higherThanSecondBoundary,
			trxSingle: [trxSingle]
		});
	}

	public updateSplitFlowState(): void {
		this.setState({
			isSplitFlow: true,
			isValidationLoading: true
		});
	}

	public updateStateSplit(
		response: PriceVerificationModel,
		trxSingle: TaxesUndeclaredTrxModel,
		declaredAmount: number
	): void {
		const newTrxArray = [...this.state.trxSingle];
		const newSplitArray = [...this.state.splitWarning];
		newTrxArray[this.state.sliceNumber] = { ...trxSingle };
		newSplitArray[this.state.sliceNumber] = {
			higherThanFirstBoundary: response.higherThanFirstBoundary,
			higherThanSecondBoundary: response.higherThanSecondBoundary
		};
		this.setState({
			amountLeft: Number.parseFloat((this.state.amountLeft - declaredAmount).toFixed(8)),
			isValidationLoading: false,
			trxSingle: newTrxArray,
			splitWarning: newSplitArray
		});
	}

	public updateStateSplitOnPriceOrCutOff(trxSingle: TaxesUndeclaredTrxModel, declaredAmount: number): void {
		const newTrxArray = [...this.state.trxSingle];
		newTrxArray[this.state.sliceNumber] = { ...trxSingle };
		this.setState({
			amountLeft: Number.parseFloat((this.state.amountLeft - declaredAmount).toFixed(8)),
			isValidationLoading: false,
			trxSingle: newTrxArray
		});
	}

	public setSliceNumber(): number {
		const sliceNumber = this.state.sliceNumber + 1;
		this.setState({
			sliceNumber: sliceNumber
		});
		return sliceNumber;
	}

	public setIsValidationLoading(isValidationLoading: boolean): void {
		this.setState({
			isValidationLoading
		});
	}

	public setCoinSwappedNonSplit(isCoinSwapped: boolean, trxSingle: TaxesUndeclaredTrxModel): void {
		this.setState({
			isCoinSwapped,
			trxSingle: [trxSingle]
		});
	}

	public setCoinSwappedSplit(isCoinSwapped: boolean): void {
		const newTrxArray = [...this.state.trxSingle];
		newTrxArray[this.state.sliceNumber] = {
			...newTrxArray[this.state.sliceNumber],
			has_this_token_been_swapped: isCoinSwapped
		};

		this.setState({
			isCoinSwapped,
			isValidationLoading: false,
			trxSingle: newTrxArray
		});
	}

	public resetState(): void {
		this.setState(initialState);
	}
}
